import {
    createContext,
    createElement,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type MutableRefObject,
    type ReactNode,
} from 'react';
import { fetchMailboxes, MAILBOX_SYNC_TRIGGERED_EVENT } from '../api/mailboxes';
import { useAuth } from '../auth/AuthContext';
import type { MailboxStatusItem, SyncStage } from '../types/mailbox';
import { isTerminalSyncStage } from '../types/mailbox';

export interface ActiveSyncJobSummary {
    mailboxId: number;
    mailboxEmail: string;
    syncJobId: string | null;
    currentStage: SyncStage | null;
    errorClass: string | null;
    /** Mailbox-row error_class from GET /mailboxes. Grant failures live here even when the job code is generic. */
    mailboxErrorClass?: string | null;
    metrics?: Record<string, unknown>;
}

export interface UseActiveSyncJobsResult {
    activeJobs: ActiveSyncJobSummary[];
    failedJobs: ActiveSyncJobSummary[];
    anyActive: boolean;
    anyFailed: boolean;
    /** oauth_expired / connection_error / provider-revoked grant — from mailbox rows. */
    anyGrantAttention?: boolean;
    /** True when at least one mailbox row is CONNECTED. Omitted until a caller has mailbox rows. */
    hasConnectedMailbox?: boolean;
    /**
     * True after at least one successful GET /mailboxes. A timeout leaves this false
     * so an empty list does not override /user/me. Later failures do not clear it.
     */
    pollSucceeded?: boolean;
    /**
     * `last_successful_sync` of each CONNECTED mailbox row (null when it never finished one).
     * Omitted until a caller has mailbox rows. Today's sync button judges staleness from it.
     */
    connectedSyncTimes?: Array<string | null>;
    loaded: boolean;
    refresh: () => void;
    error: string | null;
}

const ACTIVE_POLL_MS = 2000;
const IDLE_KEEPALIVE_MS = 30_000;

function isMailboxActive(mailbox: MailboxStatusItem): boolean {
    const job = mailbox.active_sync_job;
    return job != null && !isTerminalSyncStage(job.current_stage);
}

function isMailboxFailed(mailbox: MailboxStatusItem): boolean {
    if (isMailboxActive(mailbox)) {
        return false;
    }
    // Canonical mailbox health covers failures flagged outside the sync-job
    // pipeline (on-demand Gmail API auth failure, provider health refresh).
    if (String(mailbox.sync_health || '').toLowerCase() === 'failed') {
        return true;
    }
    return mailbox.last_sync_job?.current_stage === 'failed';
}

function toActiveSummary(mailbox: MailboxStatusItem): ActiveSyncJobSummary {
    const job = mailbox.active_sync_job!;
    return {
        mailboxId: mailbox.id,
        mailboxEmail: mailbox.email_address,
        syncJobId: job.id,
        currentStage: job.current_stage,
        errorClass: job.error_class ?? null,
        metrics: job.metrics,
    };
}

const GRANT_ATTENTION_CLASSES = new Set([
    'oauth_expired',
    'connection_error',
    'connection_revoked',
]);

function isIntentionalDisconnect(mailbox: MailboxStatusItem): boolean {
    const state = (mailbox.connection_state || '').toLowerCase();
    const status = (mailbox.status || '').toUpperCase();
    const revoked =
        state === 'revoked' ||
        status === 'REVOKED' ||
        mailbox.error_class === 'connection_revoked';
    if (!revoked) {
        return false;
    }
    // User disconnect and plan-limit disconnect write revoked_at.
    // Provider grant revocation leaves it null.
    return mailbox.revoked_at != null && mailbox.revoked_at !== '';
}

function isGrantAttention(mailbox: MailboxStatusItem): boolean {
    if (isIntentionalDisconnect(mailbox)) {
        return false;
    }
    if (mailbox.error_class && GRANT_ATTENTION_CLASSES.has(mailbox.error_class)) {
        return true;
    }
    return (mailbox.connection_state || '').toLowerCase() === 'error';
}

function isConnectedMailbox(mailbox: MailboxStatusItem): boolean {
    if ((mailbox.connection_state || '').toLowerCase() === 'connected') {
        return true;
    }
    return (mailbox.status || '').toUpperCase() === 'CONNECTED';
}

function toFailedSummary(mailbox: MailboxStatusItem): ActiveSyncJobSummary {
    const job = mailbox.last_sync_job;
    return {
        mailboxId: mailbox.id,
        mailboxEmail: mailbox.email_address,
        syncJobId: job?.id ?? null,
        currentStage: 'failed',
        errorClass: job?.error_class ?? mailbox.error_class ?? null,
        mailboxErrorClass: mailbox.error_class ?? null,
    };
}

function syncPollingRefs(
    list: MailboxStatusItem[],
    anyActiveRef: MutableRefObject<boolean>,
    anyFailedRef: MutableRefObject<boolean>,
): { anyActive: boolean; anyFailed: boolean } {
    const anyActive = list.some(isMailboxActive);
    const anyFailed = list.some(isMailboxFailed);
    anyActiveRef.current = anyActive;
    anyFailedRef.current = anyFailed;
    return { anyActive, anyFailed };
}

const ActiveSyncJobsContext = createContext<UseActiveSyncJobsResult | null>(null);

/**
 * Single shared mailbox poll for the app shell. Wrap Layout (or app root) once.
 */
export function ActiveSyncJobsProvider({ children }: { children: ReactNode }) {
    const value = useActiveSyncJobsPolling();
    return createElement(ActiveSyncJobsContext.Provider, { value }, children);
}

/**
 * Reads shared poll state from {@link ActiveSyncJobsProvider}. Do not call the polling hook
 * directly from multiple components — that duplicates /mailboxes timers.
 */
export function useActiveSyncJobs(): UseActiveSyncJobsResult {
    const value = useContext(ActiveSyncJobsContext);
    if (value == null) {
        throw new Error('useActiveSyncJobs must be used within ActiveSyncJobsProvider');
    }
    return value;
}

/**
 * Polls GET /mailboxes for shell-level sync awareness (Story 9.4).
 * Active cadence: 2s while any mailbox has a non-terminal active_sync_job.
 * Idle keepalive: 30s when no active or unresolved failed jobs.
 * Pauses while the tab is hidden or the user is unauthenticated.
 * Does not invalidate work-items page cache.
 */
function useActiveSyncJobsPolling(): UseActiveSyncJobsResult {
    const { session } = useAuth();
    const userId = session?.user?.id ?? null;

    const [mailboxes, setMailboxes] = useState<MailboxStatusItem[]>([]);
    const [loaded, setLoaded] = useState(false);
    const [pollSucceeded, setPollSucceeded] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const mountedRef = useRef(true);
    const inFlightRef = useRef(false);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const anyActiveRef = useRef(false);
    const anyFailedRef = useRef(false);

    const activeJobs = useMemo(
        () => mailboxes.filter(isMailboxActive).map(toActiveSummary),
        [mailboxes],
    );
    const failedJobs = useMemo(
        () => mailboxes.filter(isMailboxFailed).map(toFailedSummary),
        [mailboxes],
    );
    const anyActive = activeJobs.length > 0;
    const anyFailed = failedJobs.length > 0;
    const anyGrantAttention = useMemo(
        () => mailboxes.some(isGrantAttention),
        [mailboxes],
    );
    const hasConnectedMailbox = useMemo(
        () => mailboxes.some(isConnectedMailbox),
        [mailboxes],
    );
    const connectedSyncTimes = useMemo(
        () => mailboxes.filter(isConnectedMailbox).map((m) => m.last_successful_sync ?? null),
        [mailboxes],
    );

    anyActiveRef.current = anyActive;
    anyFailedRef.current = anyFailed;

    const pollIntervalMs =
        anyActive || anyFailed ? ACTIVE_POLL_MS : IDLE_KEEPALIVE_MS;

    const loadMailboxes = useCallback(async (): Promise<boolean> => {
        if (!mountedRef.current || userId == null) {
            return false;
        }
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
            return false;
        }
        if (inFlightRef.current) {
            return false;
        }
        inFlightRef.current = true;
        try {
            const response = await fetchMailboxes();
            if (!mountedRef.current) {
                return false;
            }
            const list = response.mailboxes ?? [];
            setMailboxes(list);
            syncPollingRefs(list, anyActiveRef, anyFailedRef);
            setError(null);
            setPollSucceeded(true);
            setLoaded(true);
            return true;
        } catch (err: unknown) {
            if (!mountedRef.current) {
                return false;
            }
            const message =
                err instanceof Error
                    ? err.message
                    : 'Could not load mailbox sync status.';
            setError(message);
            setLoaded(true);
            return false;
        } finally {
            inFlightRef.current = false;
        }
    }, [userId]);

    const refresh = useCallback(() => {
        void loadMailboxes();
    }, [loadMailboxes]);

    const clearPollingInterval = useCallback(() => {
        if (intervalRef.current) {
            window.clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, []);

    const schedulePolling = useCallback(() => {
        clearPollingInterval();
        if (userId == null || !mountedRef.current) {
            return;
        }
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
            return;
        }
        const ms = anyActiveRef.current || anyFailedRef.current ? ACTIVE_POLL_MS : IDLE_KEEPALIVE_MS;
        intervalRef.current = setInterval(() => {
            void loadMailboxes();
        }, ms);
    }, [clearPollingInterval, loadMailboxes, userId]);

    const handleSyncTriggered = useCallback(async () => {
        const didLoad = await loadMailboxes();
        if (!mountedRef.current || userId == null) {
            return;
        }
        if (!didLoad) {
            clearPollingInterval();
            return;
        }
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
            clearPollingInterval();
            return;
        }
        schedulePolling();
    }, [loadMailboxes, schedulePolling, clearPollingInterval, userId]);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            clearPollingInterval();
        };
    }, [clearPollingInterval]);

    useEffect(() => {
        if (userId == null) {
            setMailboxes([]);
            setLoaded(false);
            setPollSucceeded(false);
            setError(null);
            clearPollingInterval();
            return;
        }
        void loadMailboxes().then((didLoad) => {
            if (!mountedRef.current || !didLoad) {
                return;
            }
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
                clearPollingInterval();
                return;
            }
            schedulePolling();
        });
        return clearPollingInterval;
    }, [userId, pollIntervalMs, loadMailboxes, schedulePolling, clearPollingInterval]);

    useEffect(() => {
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible' && userId != null) {
                void loadMailboxes().then((didLoad) => {
                    if (!mountedRef.current || !didLoad) {
                        return;
                    }
                    schedulePolling();
                });
            } else if (document.visibilityState === 'hidden') {
                clearPollingInterval();
            }
        };
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => document.removeEventListener('visibilitychange', onVisibilityChange);
    }, [userId, loadMailboxes, schedulePolling, clearPollingInterval]);

    useEffect(() => {
        const onSyncTriggered = () => {
            void handleSyncTriggered();
        };
        window.addEventListener(MAILBOX_SYNC_TRIGGERED_EVENT, onSyncTriggered);
        return () => window.removeEventListener(MAILBOX_SYNC_TRIGGERED_EVENT, onSyncTriggered);
    }, [handleSyncTriggered]);

    return {
        activeJobs,
        failedJobs,
        anyActive,
        anyFailed,
        anyGrantAttention,
        hasConnectedMailbox,
        pollSucceeded,
        connectedSyncTimes,
        loaded,
        refresh,
        error,
    };
}
